import React from 'react';
import { Cloud, Truck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import AnimatedBackground from './AnimatedBackground';

const DivisionBlock = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const divisionBlocks = content?.divisionBlocks || [];
  const P = (v) => pickI18n(v, lang);
  return (
    <section className="relative py-20 sm:py-28 bg-gradient-to-b from-[#0d1b2a] to-[#0a1622] overflow-hidden">
      <AnimatedBackground variant="aurora" intensity={0.35} />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-4">
            {t('sections.division')}
          </div>
          <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-5xl font-light tracking-tight" style={{ lineHeight: 1.25 }}>
            {t('sections.divisionTitle')}{' '}
            <span className="italic text-[#5eead4]">{t('sections.divisionTitleHighlight')}</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-white/60 text-[15px] sm:text-[16px] max-w-xl leading-relaxed">
            {t('sections.divisionDescription')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {divisionBlocks.map((b, i) => {
            const Icon = i === 0 ? Cloud : Truck;
            return (
              <div
                key={b.id}
                className="group relative rounded-3xl overflow-hidden border border-white/5 bg-[#0a1420] hover:border-[#00e0c7]/30 transition-all duration-500"
              >
                {/* Image */}
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.title}
                    className="w-full h-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1420] via-[#0a1420]/40 to-transparent" />
                  <div className="absolute top-6 left-6 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#0d1b2a]/80 backdrop-blur-md border border-white/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#00e0c7]" />
                    </div>
                    <div className="px-3 py-1.5 rounded-full bg-[#0d1b2a]/70 backdrop-blur-md border border-white/10 text-[10px] uppercase tracking-[0.2em] text-[#9be8dc]">
                      {P(b.tag)}
                    </div>
                  </div>
                  <div className="absolute top-6 right-6 font-display text-7xl text-white/10 font-light">
                    0{i + 1}
                  </div>
                </div>

                <div className="p-8 lg:p-10">
                  <h3 className="font-display text-white text-3xl lg:text-4xl font-light tracking-tight mb-4">
                    {P(b.title)}
                  </h3>
                  <p className="text-white/60 text-[15px] leading-relaxed mb-6">
                    {P(b.description)}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {(b.bullets || []).map((bullet, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-white/75 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#00e0c7] flex-shrink-0" />
                        {P(bullet)}
                      </li>
                    ))}
                  </ul>
                  <button className="inline-flex items-center gap-2 text-[#00e0c7] text-sm font-medium group/btn">
                    {t('cta.seeMore')}
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DivisionBlock;
